// src/components/editor/ElementHoverCard.jsx
//
// The card that appears over a recognized story element name in the editor:
// its name, its type and the author's description of it.
//
// Rendered OUTSIDE the manuscript, in a portal, and anchored to the hovered
// name. The prototype nested its tooltip inside the tag, which put the
// tooltip's own words into the document — they would be counted, paginated and
// read by every innerText of the editor. Here the manuscript holds only the
// highlight wrappers (utils/elementHighlights.js), and this reads them.
//
// Listens on `document` rather than the editor host because the host mounts
// after the draft loads, and a listener attached to a ref that was still null
// would never fire. The card looks the element up by id on every render, so
// editing a description in the Story Elements panel shows in an open card.
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Fade, Popper } from '@mui/material';
import { tokens } from '../../theme';
import { ELEMENT_ID_ATTR, ELEMENT_MATCH_ATTR } from '../../utils/elementHighlights';

// The prototype's hues — character pink, item amber, location green — on the
// theme's category pairs, the same mapping the editor stylesheet tints with.
const TYPES = {
  actor: { label: 'Actor', ...tokens.category.bookKnowHow },
  artifact: { label: 'Artifact', ...tokens.category.reading },
  scene: { label: 'Scene', ...tokens.category.publishing },
};

const SELECTOR = `[${ELEMENT_ID_ATTR}]`;

/**
 * The rectangle of the whole hovered name. A name the formatting toolbar split
 * into per-character spans has one wrapper per span; the card should centre on
 * the word, not on the one letter under the pointer. Only fragments on the
 * hovered line count, so a name that wraps anchors to the half being pointed at.
 */
function nameRect(span) {
  const rect = span.getBoundingClientRect();
  const match = span.getAttribute(ELEMENT_MATCH_ATTR);
  const host = span.closest('.editable-content') || document;
  const parts = Array.from(host.querySelectorAll(`[${ELEMENT_MATCH_ATTR}="${match}"]`))
    .map((el) => el.getBoundingClientRect())
    .filter((r) => r.width > 0 && Math.abs(r.top - rect.top) < rect.height / 2);
  if (parts.length === 0) return rect;
  const left = Math.min(...parts.map((r) => r.left));
  const right = Math.max(...parts.map((r) => r.right));
  return new DOMRect(left, rect.top, right - left, rect.height);
}

export default function ElementHoverCard({ editorRef, nodes = [] }) {
  // { span, id, rect } of the last name hovered. Kept after the pointer leaves
  // so the card still has its content and its place while it fades out.
  const [hover, setHover] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const inEditor = (el) => !!(el && editorRef.current && editorRef.current.contains(el));

    const onOver = (e) => {
      const span = e.target?.closest?.(SELECTOR);
      if (!span || !inEditor(span)) return;
      // Reuse the measurement only for the very same wrapper. Every recognition
      // pass builds new wrappers and renumbers occurrences, so an occurrence
      // number can belong to a different word after a collaborator's edit;
      // the wrapper element itself cannot.
      setHover((prev) => (prev && prev.span === span ? prev : {
        span,
        id: span.getAttribute(ELEMENT_ID_ATTR),
        rect: nameRect(span),
      }));
      setOpen(true);
    };
    const onOut = (e) => {
      const from = e.target?.closest?.(SELECTOR);
      if (!from || !inEditor(from)) return;
      // Moving between the fragments of one split name is not leaving it.
      const to = e.relatedTarget?.closest?.(SELECTOR);
      if (to && to.getAttribute(ELEMENT_MATCH_ATTR) === from.getAttribute(ELEMENT_MATCH_ATTR)) return;
      setOpen(false);
    };
    // The card is placed once, from a measured rectangle, so anything that
    // moves the text under it closes it rather than leaving it adrift. Closing
    // this way also forgets the wrapper, so hovering the same one after a
    // scroll re-measures rather than reusing a rectangle that has moved.
    const hide = () => {
      setOpen(false);
      setHover((prev) => (prev ? { ...prev, span: null } : prev));
    };

    document.addEventListener('mouseover', onOver);
    document.addEventListener('mouseout', onOut);
    document.addEventListener('keydown', hide);
    document.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      document.removeEventListener('keydown', hide);
      document.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [editorRef]);

  const node = hover ? nodes.find((n) => String(n._id) === hover.id) : null;
  const type = node ? TYPES[node.type] : null;

  const anchorEl = useMemo(
    () => (hover ? { getBoundingClientRect: () => hover.rect } : null),
    [hover],
  );

  return (
    <Popper
      open={open && !!(node && type)}
      anchorEl={anchorEl}
      placement="top"
      transition
      modifiers={[{ name: 'offset', options: { offset: [0, tokens.space.sm] } }]}
      sx={{ zIndex: tokens.z.overlay, pointerEvents: 'none' }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={tokens.motion.base}>
          <Box
            role="tooltip"
            sx={{
              width: 240,
              p: `${tokens.space.md}px`,
              bgcolor: tokens.color.purpleDeep,
              border: `1px solid ${tokens.color.lineOnDark}`,
              borderRadius: `${tokens.radius.panel}px`,
              boxShadow: tokens.shadow.md,
              fontFamily: tokens.family.sans,
              fontSize: tokens.text.label.size,
              lineHeight: tokens.text.label.lineHeight,
            }}
          >
            {node && type && (
              <>
                <Box
                  sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    gap: `${tokens.space.sm}px`, mb: `${tokens.space.xs}px`,
                  }}
                >
                  <Box
                    component="span"
                    sx={{ fontWeight: tokens.text.label.weight, color: tokens.color.purpleSoft }}
                  >
                    {node.name}
                  </Box>
                  <Box
                    component="span"
                    sx={{
                      flexShrink: 0,
                      px: `${tokens.space.xs}px`,
                      borderRadius: `${tokens.radius.base}px`,
                      bgcolor: type.bg,
                      color: type.ink,
                      fontFamily: tokens.family.mono,
                      fontSize: tokens.text.micro.size,
                      fontWeight: tokens.text.micro.weight,
                      textTransform: 'uppercase',
                    }}
                  >
                    {type.label}
                  </Box>
                </Box>
                <Box
                  sx={{
                    color: tokens.color.onDark,
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                    fontStyle: node.desc?.trim() ? 'normal' : 'italic',
                  }}
                >
                  {node.desc?.trim() || 'No description provided.'}
                </Box>
              </>
            )}
          </Box>
        </Fade>
      )}
    </Popper>
  );
}
